import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrefersReducedMotion } from '@/hooks';
import { announceToScreenReader } from '@/lib/a11y';
import { Button } from '@/components/ui/button';
import { useOnboardingStore, type TourId } from '../onboardingStore';
import { getTourSteps } from '../data/tourSteps';
import { useSpotlightPosition } from '../hooks/useSpotlightPosition';
import { SpotlightOverlay } from './SpotlightOverlay';
import { OnboardingTooltip } from './OnboardingTooltip';
import { ProgressIndicator } from './ProgressIndicator';

interface GuidedTourProps {
  tourId: TourId;
}

/**
 * Orchestrates a step-by-step guided tour with spotlight overlay and tooltips.
 * Handles keyboard navigation, scroll-into-view, and step transitions.
 */
export function GuidedTour({ tourId }: GuidedTourProps) {
  const navigate = useNavigate();
  const prefersReducedMotion = usePrefersReducedMotion();
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const activeTourStep = useOnboardingStore((s) => s.activeTourStep);
  const nextTourStep = useOnboardingStore((s) => s.nextTourStep);
  const prevTourStep = useOnboardingStore((s) => s.prevTourStep);
  const skipTour = useOnboardingStore((s) => s.skipTour);
  const completeTour = useOnboardingStore((s) => s.completeTour);

  const steps = getTourSteps(tourId);
  const currentStep = steps[activeTourStep];
  const isFirstStep = activeTourStep === 0;
  const isLastStep = activeTourStep === steps.length - 1;

  // Track target position
  const { rect: targetRect } = useSpotlightPosition(
    currentStep?.targetSelector ?? null
  );

  // Scroll target into view when step changes
  useEffect(() => {
    if (!currentStep?.targetSelector) return;

    const el = document.querySelector<HTMLElement>(currentStep.targetSelector);
    if (el) {
      el.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }, [currentStep?.targetSelector, prefersReducedMotion]);

  // Announce step to screen readers
  useEffect(() => {
    if (currentStep) {
      announceToScreenReader(
        `Tour step ${String(activeTourStep + 1)} of ${String(steps.length)}: ${currentStep.title}. ${currentStep.content}`
      );
    }
  }, [activeTourStep, currentStep, steps.length]);

  // Focus the Next button when step changes
  useEffect(() => {
    const timer = setTimeout(() => {
      nextButtonRef.current?.focus();
    }, 100);
    return () => { clearTimeout(timer); };
  }, [activeTourStep]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        skipTour();
      } else if (e.key === 'ArrowRight' || (e.key === 'Enter' && e.target === nextButtonRef.current)) {
        // ArrowRight always advances
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          if (isLastStep) {
            completeTour();
          } else {
            nextTourStep();
          }
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!isFirstStep) {
          prevTourStep();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [isFirstStep, isLastStep, nextTourStep, prevTourStep, skipTour, completeTour]);

  if (!currentStep) return null;

  const handleNext = () => {
    if (isLastStep) {
      completeTour();
    } else {
      nextTourStep();
    }
  };

  const handleFinishWithAction = (path: string) => {
    completeTour();
    void navigate(path);
  };

  return (
    <>
      <SpotlightOverlay
        targetRect={targetRect}
        padding={currentStep.highlightPadding ?? 8}
      />

      <OnboardingTooltip
        targetRect={targetRect}
        placement={currentStep.placement}
      >
        <div className="p-4">
          <h3 className="font-semibold">{currentStep.title}</h3>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
            {currentStep.content}
          </p>

          {/* Final step CTA buttons */}
          {isLastStep && (
            <div className="mt-4 flex gap-2">
              <Button
                size="sm"
                onClick={() => { handleFinishWithAction('/editor'); }}
              >
                Open Editor
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { handleFinishWithAction('/examples'); }}
              >
                Browse Examples
              </Button>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <ProgressIndicator
              totalSteps={steps.length}
              currentStep={activeTourStep}
            />

            <div className="flex items-center gap-2">
              {!isFirstStep && !isLastStep && (
                <Button size="sm" variant="ghost" onClick={prevTourStep}>
                  Back
                </Button>
              )}
              {!isLastStep && (
                <button
                  onClick={skipTour}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Skip
                </button>
              )}
              <Button
                ref={nextButtonRef}
                size="sm"
                onClick={handleNext}
              >
                {isLastStep ? 'Done' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </OnboardingTooltip>
    </>
  );
}
